import React, { useEffect } from 'react';
import ConfirmDialog from '../../../../../components/ConfirmDialog';
import WaveHandIcon from '../../../../../components/WaveHandIcon/WaveHandIcon';
import Api from '../../../../../api';
import { acquireChatbaseUiHidden } from '../../../../../components/ChatWidget';

interface NoShiftsModalProps {
  open: boolean;
  userCity?: string;
  onClose: () => void;
}

export const NoShiftsModal: React.FC<NoShiftsModalProps> = ({ open, userCity = 'your area', onClose }) => {
  useEffect(() => {
    if (open) return acquireChatbaseUiHidden();
  }, [open]);

  const handleTurnOnNotifications = async () => {
    try {
      // Mark as seen immediately
      await Api.updateHygienist({ isNoShiftsAvailableModalSeen: 1 });

      if ((window as any).cordova) {
        const cordova = (window as any).cordova;
        if (cordova.plugins && cordova.plugins.diagnostic) {
          try {
            await cordova.plugins.diagnostic.switchToSettings();
            console.log('[NoShiftsModal] Opened device settings for notifications');
          } catch (error) {
            console.error('[NoShiftsModal] Failed to open device settings:', error);
            alert('Unable to open device settings. Please enable notifications manually in your device settings.');
          }
        } else {
          console.error('[NoShiftsModal] Diagnostic plugin not available');
          alert('Unable to open device settings. Please enable notifications manually in your device settings.');
        }
      } else {
        // Browser: Request notification permission
        if ('Notification' in window) {
          try {
            // eslint-disable-next-line compat/compat -- feature-detected by the 'Notification' in window check
            const permission = await Notification.requestPermission();
            if (permission === 'granted') {
              console.log('[NoShiftsModal] Browser notification permission granted');
              alert('Notifications enabled successfully!');
            } else if (permission === 'denied') {
              console.log('[NoShiftsModal] Browser notification permission denied');
              alert('Notification permission was denied. You can enable notifications in your browser settings.');
            } else {
              console.log('[NoShiftsModal] Browser notification permission dismissed');
            }
          } catch (error) {
            console.error('[NoShiftsModal] Failed to request browser notifications:', error);
            alert('Unable to request notification permission. Please check your browser settings.');
          }
        } else {
          console.error('[NoShiftsModal] Browser notifications not supported');
          alert('Notifications are not supported in this browser.');
        }
      }
    } catch (e) {
      console.error('[NoShiftsModal] Failed to persist isNoShiftsAvailableModalSeen', e);
    } finally {
      onClose();
    }
  };

  const handleClose = async () => {
    try {
      await Api.updateHygienist({ isNoShiftsAvailableModalSeen: 1 });
    } catch (e) {
      console.error('[NoShiftsModal] Failed to persist isNoShiftsAvailableModalSeen', e);
    }
    onClose();
  };

  const content = (
    <div style={{ padding: '8px', fontSize: '14px' }}>
      {/* Header Section */}
      <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
        <div style={{ width: '56px', height: '56px', backgroundColor: '#e3f2fd', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '16px', flexShrink: 0 }}>
          <WaveHandIcon size="28px" />
        </div>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <h3 style={{ fontSize: '22px', fontWeight: 700, color: '#333', marginBottom: '6px', marginTop: 0 }}>
            Welcome!
          </h3>
          <p style={{ fontSize: '14px', color: '#0B4598', fontWeight: 600, margin: 0 }}>
            TempStars is still growing in {userCity}
          </p>
        </div>
        <div style={{ width: '56px', marginLeft: '16px', flexShrink: 0 }}></div>
      </div>
      
      {/* Main Message */}
      <div style={{ marginBottom: '16px' }}>
        <p style={{ color: '#666', lineHeight: 1.6, marginBottom: '16px' }}>
          There are no shifts right now, <strong style={{ color: '#333' }}>but don't worry!</strong> TempStars is ramping up in the {userCity} area and things are taking off - new dental offices are joining and posting every week.
        </p>
      </div>
  
      {/* Trust Card */}
      <div style={{ backgroundColor: '#fff', margin: '0 0 16px 0', boxShadow: '0 3px 12px rgba(11, 69, 152, 0.2)', border: '2px solid #0B4598', borderRadius: '12px', padding: '20px' }}>
        
        {/* Trust Header */}
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <div style={{ width: '48px', height: '48px', backgroundColor: '#0B4598', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: '8px', fontSize: '24px' }}>
            🏆
          </div>
          <p style={{ fontSize: '16px', color: '#0B4598', fontWeight: 700, margin: 0 }}>
            You can trust us to deliver
          </p>
        </div>
        
        {/* Trust Stats Box */}
        <div style={{ backgroundColor: '#f5f5f5', padding: '16px', borderRadius: '8px' }}>
          <div style={{ display: 'grid', gap: '12px' }}>
            
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <span style={{ color: '#0B4598', fontSize: '20px', marginRight: '12px', lineHeight: 1 }}>✓</span>
              <p style={{ fontSize: '14px', color: '#333', margin: 0, lineHeight: 1.5, textAlign: 'left' }}>
                <strong>Operating Since 2015</strong> 
              </p>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <span style={{ color: '#0B4598', fontSize: '20px', marginRight: '12px', lineHeight: 1 }}>✓</span>
              <p style={{ fontSize: '14px', color: '#333', margin: 0, lineHeight: 1.5, textAlign: 'left' }}>
                <strong>Trusted by 34,000+ dental professionals</strong> 
              </p>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <span style={{ color: '#0B4598', fontSize: '20px', marginRight: '12px', lineHeight: 1 }}>✓</span>
              <p style={{ fontSize: '14px', color: '#333', margin: 0, lineHeight: 1.5, textAlign: 'left' }}>
                <strong>$42 million dental temping income earned by dental professionals like you</strong>
              </p>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <span style={{ color: '#0B4598', fontSize: '20px', marginRight: '12px', lineHeight: 1 }}>✓</span>
              <p style={{ fontSize: '14px', color: '#333', margin: 0, lineHeight: 1.5, textAlign: 'left' }}>
                <strong>150,000+ successful dental temping shifts completed</strong>
              </p>
            </div>
            
          </div>
        </div>
      </div>
  
      {/* Commitment Statement */}
      <div style={{ marginBottom: '16px' }}>
        <p style={{ color: '#333', lineHeight: 1.6, fontWeight: 600, textAlign: 'center' }}>
          We know what we're doing, and we're committed to giving you the best temping and hiring opportunities. So hang tight, we're here for you!
        </p>
      </div>
  
      {/* Helper Text */}
      <p style={{ fontSize: '13px', color: '#999', textAlign: 'center', margin: '0 0 16px 0' }}>
        Be the first to know when new shifts get posted!
      </p>
    </div>
  );

  return (
    <ConfirmDialog
      open={open}
      onClose={handleClose}
      title={null} // No title in confirm dialog because we have custom header in content
      content={content}
      actions={[
        {
          label: '🔔 Turn On Notifications',
          variant: 'contained',
          onClick: handleTurnOnNotifications
        },
        {
          label: 'Close',
          onClick: handleClose
        }
      ]}
    />
  );
};
